import { useState } from 'react';
import type { HostPublicProfile } from '@/mocks/hostProfile';

interface Props {
  profile: HostPublicProfile;
}

export default function HostFeedSection({ profile }: Props) {
  const [likedPosts, setLikedPosts] = useState<Set<number>>(new Set());

  const toggleLike = (postId: number) => {
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const posts = profile.posts.filter((p) => p.status === 'publicado');

  if (posts.length === 0) {
    return null;
  }

  return (
    <section id="publicaciones" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-article-line text-accent-500"></i>
          Publicaciones
        </h2>
        <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">
          {posts.length} publicaciones
        </span>
      </div>

      <div className="space-y-4">
        {posts.map((post) => {
          const isLiked = likedPosts.has(post.id);

          return (
            <div key={post.id} className="bg-white rounded-xl border border-foreground-100 overflow-hidden">
              {post.imageUrl && (
                <div className="relative w-full h-48 md:h-64 overflow-hidden">
                  <img
                    src={post.imageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  {post.isFeatured && (
                    <span className="absolute top-3 left-3 bg-accent-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Destacado
                    </span>
                  )}
                  {post.isSponsored && (
                    <span className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Patrocinado
                    </span>
                  )}
                </div>
              )}

              <div className="p-4 md:p-5">
                {!post.imageUrl && (post.isFeatured || post.isSponsored) && (
                  <div className="flex items-center gap-2 mb-2">
                    {post.isFeatured && (
                      <span className="bg-accent-100 text-accent-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Destacado</span>
                    )}
                    {post.isSponsored && (
                      <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Patrocinado</span>
                    )}
                  </div>
                )}
                <h3 className="font-semibold text-foreground-950 text-sm">{post.title}</h3>
                <p className="text-foreground-600 text-sm mt-1.5 leading-relaxed">{post.text}</p>

                {post.type === 'encuesta' && post.pollData && (
                  <div className="mt-4 space-y-2">
                    {post.pollData.options.map((opt) => {
                      const total = post.pollData!.options.reduce((s, o) => s + o.votes, 0);
                      const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                      return (
                        <div key={opt.id} className="relative">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-foreground-700">{opt.text}</span>
                            <span className="text-foreground-500 font-medium">{pct}%</span>
                          </div>
                          <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-accent-500 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Engagement */}
                <div className="flex items-center gap-5 mt-4 pt-3 border-t border-foreground-100">
                  <button
                    onClick={() => toggleLike(post.id)}
                    className={`flex items-center gap-1.5 text-xs cursor-pointer transition-colors ${isLiked ? 'text-red-500' : 'text-foreground-500 hover:text-red-500'}`}
                  >
                    <i className={isLiked ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                    <span>{isLiked ? post.engagement.likes + 1 : post.engagement.likes}</span>
                  </button>
                  <button className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">
                    <i className="ri-chat-1-line"></i>
                    <span>{post.engagement.comments}</span>
                  </button>
                  <button className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                    <span>{post.engagement.shares}</span>
                  </button>
                  <span className="ml-auto text-[10px] text-foreground-400">
                    {new Date(post.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}